import {Box} from "@mui/material";
import * as React from "react";
import {useMemo, useState} from "react";
import AuthBanner from "./AuthBanner";
import {AuthService} from "crqlar-react-common/dist/services/auth/auth.service";
import {useRedirect} from "../../utils/redirect.util";
import {useNavigate, useSearchParams} from "react-router-dom";
import LoginForm from "crqlar-react-common/dist/components/LoginForm";
import {Consent} from "crqlar-react-common/dist/models/identity/consent.model";
import ConsentDialog from "./ConsentDialog";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useDispatch} from "react-redux";
import {Settings} from "luxon";
import {AuthActions} from "crqlar-react-common/dist/state/auth.reducer";
import DefaultLogo from '../../assets/media/images/Crqlar Transparent Logo claim.png';
import {syncMixpanelAuth} from "../../utils/analytics/mixpanelAuth";
import {applyUserLanguage} from "../../utils/applyUserLanguage";

const LoginScreen = () => {

    useTrackScreenVisit('login')
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [consentsToAccept, setConsentsToAccept] = useState<Consent[]>([])
    const redirect = useRedirect()
    const decoded = useMemo(() => {
        try {
            const auth = searchParams.get('auth')
            if (!auth) return null
            return JSON.parse(atob(auth)) as { email: string; password: string }
        } catch {
            return null
        }
    }, [searchParams])

    const logoUrl = decoded ? (searchParams.get('logo') || DefaultLogo) : undefined

    return (
        <Box
            sx={{
                display: 'flex'
            }}
        >
            <AuthBanner/>
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                boxShadow: '0 0 10px 0 rgba(0,0,0,0.1)'
            }}>
                <LoginForm
                    showReset={true}
                    showDemoCredentialsTip={true}
                    logoUrl={logoUrl}
                    initialEmail={decoded?.email}
                    initialPassword={decoded?.password}
                    // Instant dev login: ?auth=<base64 {email,password}>&autoLogin=1 signs in
                    // without a click (used by coding agents for browser verification — see
                    // AGENTS.md "Backend & login"). Development-only so production ?auth=
                    // prefill links keep requiring a manual sign-in.
                    autoSubmit={process.env.NODE_ENV === 'development' && !!decoded && searchParams.get('autoLogin') === '1'}
                    executeLogin={async (email, password) => {
                        const claims = await AuthService.login(email, password)

                        if (claims?.token) {
                            if (claims.user) {
                                const token = claims.token

                                localStorage.setItem('token', token)
                                Settings.defaultZone = (claims?.organization?.address?.timeZone as string) ?? 'Europe/Vienna'

                                dispatch({
                                    type: AuthActions.SET_USER,
                                    payload: {user: claims.user, organization: claims.organization}
                                })
                                syncMixpanelAuth(claims)
                                applyUserLanguage(claims.user)

                                if (claims.consentsToAccept?.length) {
                                    setConsentsToAccept(claims.consentsToAccept)
                                } else {
                                    if (!redirect()) {
                                        navigate('/')
                                    }
                                }

                            } else {
                                throw new Error('User not found')
                            }
                        } else {
                            throw new Error('Invalid credentials')
                        }
                    }}
                />
            </Box>

            <ConsentDialog
                open={!!consentsToAccept.length}
                consentsToAccept={consentsToAccept}
                onAccept={() => {
                    setConsentsToAccept([])
                    if (!redirect()) {
                        navigate('/')
                    }
                }}
            />

        </Box>
    );
}

export default LoginScreen;
